import { Font } from '@react-pdf/renderer';
import type Markdown from 'markdown-to-jsx';
import { ComponentProps } from 'react';

import {
  Anchor,
  Blockquote,
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  HorizontalRule,
  InlineCode,
  Italic,
  LineBreak,
  Paragraph,
  Span,
  Strikethrough,
  Underline,
} from './components';
// cannot import & export list components from components.tsx due to circular dependency
import { ListItem, OrderedList, UnorderedList } from './listComponents';

// markdown-to-jsx only exposes the MarkdownToJSX namespace through its
// import-condition typings; derive Options from the component props so the
// type resolves under both the import and require conditions.
type MarkdownToJSXOptions = NonNullable<
  ComponentProps<typeof Markdown>['options']
>;

Font.register({
  family: 'Inter',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyeMZhrib2Bg-4.ttf',
      fontWeight: 100,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyfMZhrib2Bg-4.ttf',
      fontWeight: 200,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuOKfMZhrib2Bg-4.ttf',
      fontWeight: 300,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuI6fMZhrib2Bg-4.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYMZhrib2Bg-4.ttf',
      fontWeight: 600,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYMZhrib2Bg-4.ttf',
      fontWeight: 700,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyYMZhrib2Bg-4.ttf',
      fontWeight: 800,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuBWYMZhrib2Bg-4.ttf',
      fontWeight: 900,
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-100-italic.woff',
      fontWeight: 100,
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-200-italic.woff',
      fontWeight: 200,
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-300-italic.woff',
      fontWeight: 300,
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-400-italic.woff',
      fontWeight: 400,
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-500-italic.woff',
      fontWeight: 500,
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-600-italic.woff',
      fontWeight: 600,
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-700-italic.woff',
      fontWeight: 700,
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-800-italic.woff',
      fontWeight: 800,
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-900-italic.woff',
      fontWeight: 900,
      fontStyle: 'italic',
    },
  ],
});

Font.registerHyphenationCallback((word: string) => [word]);

export const MarkdownOptions: MarkdownToJSXOptions = {
  enforceAtxHeadings: true,
  forceBlock: true,
  overrides: {
    h1: { component: Heading1 },
    h2: { component: Heading2 },
    h3: { component: Heading3 },
    h4: { component: Heading4 },
    h5: { component: Heading5 },
    ul: { component: UnorderedList },
    ol: { component: OrderedList },
    li: { component: ListItem },
    br: { component: LineBreak },
    hr: { component: HorizontalRule },
    blockquote: { component: Blockquote },

    // -- inline elements --
    p: { component: Paragraph },
    span: { component: Span },
    strong: { component: Bold },
    em: { component: Italic },
    u: { component: Underline },
    del: { component: Strikethrough },
    code: { component: InlineCode },
    a: { component: Anchor },
  },
};
